import { api } from './api';

/**
 * API Service - Uses axiosInstance with interceptors
 * Token expiration (401) is handled automatically by response interceptor
 */
class ApiService {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  }

  setToken(token) {
    localStorage.setItem("auth_token", token);
  }

  clearToken() {
    localStorage.removeItem("auth_token");
  }

  async request(endpoint, options = {}) {
    const cacheKey = `${endpoint}-${JSON.stringify(options)}`;
    const method = options.method || 'GET';

    // Only cache GET requests (in-memory only)
    if (method === 'GET') {
      const cached = this.cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        return cached.data;
      }

      // Prevent duplicate requests
      if (this.pendingRequests.has(cacheKey)) {
        return await this.pendingRequests.get(cacheKey);
      }
    }

    const requestPromise = this.executeRequest(endpoint, options, cacheKey, method);

    if (method === 'GET') {
      this.pendingRequests.set(cacheKey, requestPromise);
    }

    return requestPromise;
  }

  async executeRequest(endpoint, options, cacheKey, method) {
    try {
      let response;

      switch (method) {
        case 'GET':
          response = await api.get(endpoint);
          break;
        case 'POST':
          response = await api.post(endpoint, options.body ? JSON.parse(options.body) : options.data);
          break;
        case 'PUT':
          response = await api.put(endpoint, options.body ? JSON.parse(options.body) : options.data);
          break;
        case 'PATCH':
          response = await api.patch(endpoint, options.body ? JSON.parse(options.body) : options.data);
          break;
        case 'DELETE':
          response = await api.delete(endpoint);
          break;
        default:
          response = await api.get(endpoint);
      }

      const data = response.data;

      // Cache GET requests (in-memory only)
      if (method === 'GET') {
        const cacheData = { data, timestamp: Date.now() };
        this.cache.set(cacheKey, cacheData);
        this.pendingRequests.delete(cacheKey);
      }

      return data;
    } catch (error) {
      this.pendingRequests.delete(cacheKey);

      // Return cached data if available on error (for GET requests)
      if (method === 'GET') {
        const cached = this.cache.get(cacheKey);
        if (cached) {
          console.warn('API failed, using cached data');
          return cached.data;
        }
      }

      console.error(`Request to ${endpoint} failed:`, error);
      throw error;
    }
  }

  clearCache() {
    this.cache.clear();
  }

  getAll(endpoint) {
    return this.request(endpoint);
  }

  getById(endpoint, id) {
    return this.request(`${endpoint}/${id}`);
  }

  create(endpoint, data) {
    return this.request(endpoint, { method: "POST", data });
  }

  update(endpoint, id, data) {
    return this.request(`${endpoint}/${id}`, { method: "PUT", data });
  }

  delete(endpoint, id) {
    return this.request(`${endpoint}/${id}`, { method: "DELETE" });
  }

  upload(endpoint, formData) {
    return api.upload(endpoint, formData);
  }
}

export default new ApiService();
